import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { 
  collection, 
  onSnapshot, 
  doc, 
  setDoc
} from 'firebase/firestore';

const DataContext = createContext();

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    about: { chairman: {}, directors: [] },
    products: [],
    strengths: [],
    news: { latest: [], upcoming: [], press: [] },
    awards: [],
    gallery: { images: [], videos: [] },
    inquiries: []
  });

  useEffect(() => {
    setLoading(true);
    
    // Listen to About data (Chairman)
    const unsubAbout = onSnapshot(doc(db, 'site_content', 'about'), (doc) => {
      if (doc.exists()) {
        setData(prev => ({ 
          ...prev, 
          about: { ...prev.about, chairman: doc.data().chairman || {} } 
        }));
      }
    });

    // Listen to Directors collection
    const unsubDirectors = onSnapshot(collection(db, 'directors'), (snapshot) => {
      const directors = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setData(prev => ({ 
        ...prev, 
        about: { ...prev.about, directors: directors } 
      }));
    });

    // Listen to Products (Pure Firestore Data - No static fallback)
    const unsubProducts = onSnapshot(collection(db, 'products'), (snapshot) => {
      const products = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      products.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setData(prev => ({ 
        ...prev, 
        products: products 
      }));
    });

    // Listen to Strengths (Pure Firestore Data - No static fallback)
    const unsubStrengths = onSnapshot(collection(db, 'strengths'), (snapshot) => {
      const strengthsList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setData(prev => ({ 
        ...prev, 
        strengths: strengthsList 
      }));
    });

    // Listen to News
    const unsubNews = onSnapshot(collection(db, 'news'), (snapshot) => {
      const newsItems = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setData(prev => ({ 
        ...prev, 
        news: {
          latest: newsItems.filter(i => i.section === 'latest'),
          upcoming: newsItems.filter(i => i.section === 'upcoming'),
          press: newsItems.filter(i => i.section === 'press'),
        }
      }));
    });

    // Listen to Awards
    const unsubAwards = onSnapshot(collection(db, 'awards'), (snapshot) => {
      const awardItems = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setData(prev => ({ 
        ...prev, 
        awards: awardItems
      }));
    });

    // Listen to Gallery (Document + Individual Collections for unlimited scaling)
    let docGallery = { images: [], videos: [] };
    let colImages = [];
    let colVideos = [];

    const updateGalleryState = () => {
      const allImages = [
        ...(docGallery.images || []).map(img => ({ ...img })),
        ...colImages
      ];
      const allVideos = [
        ...(docGallery.videos || []).map(vid => ({ ...vid })),
        ...colVideos
      ];

      const uniqueImages = Array.from(new Map(allImages.map(i => [String(i.id), i])).values());
      const uniqueVideos = Array.from(new Map(allVideos.map(v => [String(v.id), v])).values());

      setData(prev => ({
        ...prev,
        gallery: { images: uniqueImages, videos: uniqueVideos }
      }));
    };

    const unsubGalleryDoc = onSnapshot(doc(db, 'site_content', 'gallery'), (d) => {
      if (d.exists()) {
        docGallery = d.data();
      } else {
        docGallery = { images: [], videos: [] };
      }
      updateGalleryState();
    });

    const unsubGalleryImages = onSnapshot(collection(db, 'gallery_images'), (snapshot) => {
      colImages = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      updateGalleryState();
    });

    const unsubGalleryVideos = onSnapshot(collection(db, 'gallery_videos'), (snapshot) => {
      colVideos = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      updateGalleryState();
    });

    // Listen to Growth Stats
    const unsubStats = onSnapshot(doc(db, 'site_content', 'stats'), (doc) => {
      if (doc.exists() && doc.data().items) {
        setData(prev => ({ ...prev, stats: doc.data().items }));
      }
    });

    // Listen to Inquiries
    const unsubInquiries = onSnapshot(collection(db, 'inquiries'), (snapshot) => {
      const inquiries = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setData(prev => ({ ...prev, inquiries }));
    });

    setLoading(false);

    return () => {
      unsubAbout();
      unsubDirectors();
      unsubProducts();
      unsubStrengths();
      unsubNews();
      unsubAwards();
      unsubGalleryDoc();
      unsubGalleryImages();
      unsubGalleryVideos();
      unsubStats();
      unsubInquiries();
    };
  }, []);

  // Universal Update Function that syncs back to Firestore
  const updateData = async (newData) => {
    try {
      // Sync About
      await setDoc(doc(db, 'site_content', 'about'), newData.about);
      
      // Sync Gallery
      await setDoc(doc(db, 'site_content', 'gallery'), newData.gallery);

      setData(newData);
    } catch (error) {
      console.error("Error updating data:", error);
    }
  };

  return (
    <DataContext.Provider value={{ data, updateData, loading }}>
      {!loading && children}
    </DataContext.Provider>
  );
};
